import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocalizedCurrency, LocalizedDate } from '../localization/LocalizedComponents';

interface Gift {
  id: string;
  code: string;
  tier: {
    name: string;
    amount: number;
  };
  months: number;
  status: string;
  purchasedAt?: string;
  redeemedAt?: string;
  expiresAt?: string;
  recipientEmail?: string;
  recipientName?: string;
  purchaser?: {
    username: string;
    avatar: string | null;
  };
  creatorProfile: {
    id: string;
    displayName: string;
    avatar: string | null;
  };
  currency: string;
  amount: number;
  message?: string;
}

type TabType = 'purchased' | 'received';

export const GiftHistoryPage: React.FC = () => {
  const { t } = useTranslation('common');
  const [activeTab, setActiveTab] = useState<TabType>('purchased');
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGift, setSelectedGift] = useState<Gift | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchGifts();
  }, [activeTab]);

  const fetchGifts = async () => {
    setLoading(true);
    try {
      const endpoint = activeTab === 'purchased' 
        ? '/api/gifts/purchased'
        : '/api/gifts/received';
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      setGifts(data.gifts || []);
    } catch (error) {
      console.error('Failed to fetch gifts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (giftId: string) => {
    try {
      const response = await fetch(`/api/gifts/${giftId}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Gift email resent successfully!');
      } else {
        alert('Failed to resend email');
      }
    } catch (error) {
      console.error('Failed to resend email:', error);
      alert('Failed to resend email');
    }
  };

  const handleCancelGift = async (giftId: string) => {
    if (!confirm('Are you sure you want to cancel this gift? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/gifts/${giftId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Gift cancelled successfully');
        fetchGifts();
      } else {
        alert('Failed to cancel gift');
      }
    } catch (error) {
      console.error('Failed to cancel gift:', error);
      alert('Failed to cancel gift');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      PENDING_PAYMENT: { label: 'Pending', class: 'bg-yellow-100 text-yellow-800' },
      ACTIVE: { label: 'Active', class: 'bg-green-100 text-green-800' },
      REDEEMED: { label: 'Redeemed', class: 'bg-blue-100 text-blue-800' },
      EXPIRED: { label: 'Expired', class: 'bg-red-100 text-red-800' },
      CANCELED: { label: 'Cancelled', class: 'bg-gray-100 text-gray-800' },
      REFUNDED: { label: 'Refunded', class: 'bg-purple-100 text-purple-800' },
    };

    const badge = badges[status as keyof typeof badges] || badges.ACTIVE;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const showGiftDetails = (gift: Gift) => {
    setSelectedGift(gift);
    setShowDetailsModal(true);
  };

  const GiftDetailsModal = () => {
    if (!selectedGift) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
            <h3 className="text-xl font-bold">Gift Details</h3>
            <button
              onClick={() => setShowDetailsModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Creator & Tier */}
            <div className="flex items-center space-x-4 pb-4 border-b">
              {selectedGift.creatorProfile.avatar && (
                <img
                  src={selectedGift.creatorProfile.avatar}
                  alt={selectedGift.creatorProfile.displayName}
                  className="w-12 h-12 rounded-full"
                />
              )}
              <div className="flex-1">
                <p className="text-sm text-gray-600">Creator</p>
                <p className="font-bold">{selectedGift.creatorProfile.displayName}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedGift.tier.name}</p>
              </div>
              {getStatusBadge(selectedGift.status)}
            </div>

            {/* Gift Code */}
            <div>
              <p className="text-sm font-medium text-gray-600 mb-2">Gift Code</p>
              <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                <code className="text-lg font-mono font-bold text-gray-900">{selectedGift.code}</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(selectedGift.code);
                    alert('Code copied to clipboard!');
                  }}
                  className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                >
                  Copy
                </button>
              </div>
            </div>

            {/* Duration & Value */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Duration</p>
                <p className="text-lg font-bold">{selectedGift.months} month{selectedGift.months !== 1 ? 's' : ''}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Value</p>
                <p className="text-lg font-bold text-orange-600">
                  <LocalizedCurrency amount={selectedGift.amount} currency={selectedGift.currency} />
                </p>
              </div>
            </div>

            {/* Dates */}
            <div className="space-y-2">
              {selectedGift.purchasedAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Purchased:</span>
                  <span className="font-medium"><LocalizedDate date={selectedGift.purchasedAt} format="long" /></span>
                </div>
              )}
              {selectedGift.redeemedAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Redeemed:</span>
                  <span className="font-medium"><LocalizedDate date={selectedGift.redeemedAt} format="long" /></span>
                </div>
              )}
              {selectedGift.expiresAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Expires:</span>
                  <span className="font-medium"><LocalizedDate date={selectedGift.expiresAt} format="long" /></span>
                </div>
              )}
            </div>

            {/* Recipient/Purchaser Info */}
            {activeTab === 'purchased' && selectedGift.recipientEmail && (
              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">Recipient</p>
                <div className="bg-gray-50 rounded-lg p-3">
                  {selectedGift.recipientName && (
                    <p className="font-medium">{selectedGift.recipientName}</p>
                  )}
                  <p className="text-sm text-gray-600">{selectedGift.recipientEmail}</p>
                </div>
              </div>
            )}

            {activeTab === 'received' && selectedGift.purchaser && (
              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">From</p>
                <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-3">
                  {selectedGift.purchaser.avatar && (
                    <img
                      src={selectedGift.purchaser.avatar}
                      alt={selectedGift.purchaser.username}
                      className="w-10 h-10 rounded-full"
                    />
                  )}
                  <p className="font-medium">{selectedGift.purchaser.username}</p>
                </div>
              </div>
            )}

            {/* Personal Message */}
            {selectedGift.message && (
              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">Personal Message</p>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-800 italic">"{selectedGift.message}"</p>
                </div>
              </div>
            )}

            {/* Actions */}
            {activeTab === 'purchased' && (
              <div className="flex gap-3 pt-4 border-t">
                {selectedGift.status === 'ACTIVE' && (
                  <>
                    <button
                      onClick={() => {
                        handleResendEmail(selectedGift.id);
                        setShowDetailsModal(false);
                      }}
                      className="flex-1 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg font-medium hover:bg-orange-200 transition-colors"
                    >
                      Resend Email
                    </button>
                    <button
                      onClick={() => {
                        handleCancelGift(selectedGift.id);
                        setShowDetailsModal(false);
                      }}
                      className="flex-1 px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors"
                    >
                      Cancel Gift
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gift Memberships</h1>
        <p className="text-gray-600">View and manage your gift memberships</p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('purchased')}
              className={`${
                activeTab === 'purchased'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Gifts Purchased
            </button>
            <button
              onClick={() => setActiveTab('received')}
              className={`${
                activeTab === 'received'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Gifts Received
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      ) : gifts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {activeTab === 'purchased' ? 'No gifts purchased' : 'No gifts received'}
          </h3>
          <p className="text-gray-600 mb-6">
            {activeTab === 'purchased'
              ? "You haven't purchased any gift memberships yet"
              : "You haven't received any gift memberships yet"}
          </p>
          {activeTab === 'purchased' && (
            <Link
              to="/creators"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
            >
              Browse Creators
            </Link>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {gifts.map((gift) => (
            <div
              key={gift.id}
              onClick={() => showGiftDetails(gift)}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer overflow-hidden"
            >
              {/* Card Header */}
              <div className="bg-gradient-to-r from-orange-500 to-pink-600 p-4 text-white">
                <div className="flex items-start justify-between mb-2">
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z" clipRule="evenodd" />
                    <path d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
                  </svg>
                  {getStatusBadge(gift.status)}
                </div>
                <p className="font-bold text-lg">{gift.tier.name}</p>
                <p className="text-orange-100 text-sm">{gift.months} month{gift.months !== 1 ? 's' : ''}</p>
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-3">
                {/* Creator */}
                <div className="flex items-center space-x-2">
                  {gift.creatorProfile.avatar && (
                    <img
                      src={gift.creatorProfile.avatar}
                      alt={gift.creatorProfile.displayName}
                      className="w-8 h-8 rounded-full"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {gift.creatorProfile.displayName}
                    </p>
                  </div>
                </div>

                {/* Value */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Value:</span>
                  <span className="font-bold text-orange-600">
                    <LocalizedCurrency amount={gift.amount} currency={gift.currency} />
                  </span>
                </div>

                {/* Date */}
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">
                    {gift.purchasedAt ? 'Purchased:' : gift.redeemedAt ? 'Redeemed:' : 'Date:'}
                  </span>
                  <span className="text-gray-700">
                    <LocalizedDate date={gift.purchasedAt || gift.redeemedAt || gift.expiresAt!} format="short" />
                  </span>
                </div>

                {/* Recipient/Sender */}
                {activeTab === 'purchased' && gift.recipientEmail && (
                  <div className="text-sm">
                    <span className="text-gray-600">To: </span>
                    <span className="text-gray-900">{gift.recipientName || gift.recipientEmail}</span>
                  </div>
                )}

                {activeTab === 'received' && gift.purchaser && (
                  <div className="text-sm">
                    <span className="text-gray-600">From: </span>
                    <span className="text-gray-900">{gift.purchaser.username}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && <GiftDetailsModal />}
    </div>
  );
};
